import type React from 'react';
import { useState } from 'react';
import type { StateNexusData } from '../services/api';
import ProgressBar from './ProgressBar';
import RegistrationBanner from './RegistrationBanner';

const RED = '#E11D48';
const AMBER = '#D97706';
const GREEN = '#16A34A';
const TEAL = '#00A0A8';

function fmt(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Number(n).toLocaleString()}`;
}

function SectionHeader({ title, right }: { title: string; right?: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 12px',
        background: 'linear-gradient(90deg, rgba(0,160,168,0.08) 0%, rgba(0,160,168,0.02) 100%)',
        borderLeft: `3px solid ${TEAL}`,
        borderRadius: '0 6px 6px 0',
        marginBottom: 12,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: TEAL,
          textTransform: 'uppercase',
          letterSpacing: '.06em',
        }}
      >
        {title}
      </span>
      {right}
    </div>
  );
}

function RevenueSection({
  data,
  pct,
  accentColor,
}: { data: StateNexusData; pct: number; accentColor: string }): JSX.Element {
  const [showSplit, setShowSplit] = useState(true);
  const total = (data.revenue ?? 0) + (data.mfRevenue ?? 0);

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E2E8F0',
        borderRadius: 10,
        overflow: 'hidden',
        marginBottom: 10,
      }}
    >
      <SectionHeader
        title="Revenue Threshold"
        right={
          data.mfExcluded && (
            <button
              onClick={() => setShowSplit((p) => !p)}
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#fff',
                background: TEAL,
                border: 'none',
                borderRadius: 5,
                padding: '3px 9px',
                cursor: 'pointer',
                letterSpacing: '.03em',
              }}
            >
              DIRECT VS MARKETPLACE
            </button>
          )
        }
      />
      <div style={{ padding: '0 14px 14px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 2,
          }}
        >
          <span style={{ fontSize: 26, fontWeight: 800, color: '#0F172A' }}>
            {fmt(data.revenue)}
          </span>
          <span style={{ fontSize: 12, color: '#94A3B8' }}>
            / {fmt(data.revenueThreshold)} threshold
          </span>
        </div>
        <div style={{ textAlign: 'right', marginBottom: 2 }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: accentColor }}>
            {pct.toFixed(1)}%
          </span>
        </div>
        <ProgressBar pct={pct} color={accentColor} />

        {data.mfExcluded && showSplit && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: '#64748B' }}>Total sales (all channels)</span>
              <span style={{ fontWeight: 600, color: '#0F172A' }}>{fmt(total)}</span>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: '#94A3B8' }}>Amazon / marketplace sales</span>
                <span style={{ color: '#94A3B8' }}>– {fmt(data.mfRevenue)}</span>
              </div>
              <div style={{ fontSize: 10, color: GREEN, marginTop: 2, paddingLeft: 2 }}>
                ← Excluded — marketplace facilitator law ({data.stateCode})
              </div>
            </div>
            <div style={{ paddingTop: 7, borderTop: '1px dashed #E2E8F0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: '#0F172A', fontWeight: 600 }}>Your direct sales</span>
                <span style={{ fontWeight: 700, color: '#0F172A' }}>{fmt(data.directRevenue)}</span>
              </div>
              <div style={{ fontSize: 10, color: TEAL, marginTop: 2, paddingLeft: 2 }}>
                ← THIS triggered nexus
              </div>
            </div>
          </div>
        )}

        {!data.mfExcluded && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 12,
              marginTop: 8,
              paddingTop: 8,
              borderTop: '1px solid #F1F5F9',
            }}
          >
            <span style={{ color: '#64748B' }}>Your direct sales</span>
            <span style={{ fontWeight: 600, color: '#0F172A' }}>
              {fmt(data.directRevenue ?? data.revenue)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function TransactionSection({
  data,
  accentColor,
}: { data: StateNexusData; accentColor: string }): JSX.Element {
  const count = data.transactionCount ?? 0;
  const limit = data.transactionThreshold ?? 200;
  const pct = (count / limit) * 100;

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E2E8F0',
        borderRadius: 10,
        overflow: 'hidden',
        marginBottom: 10,
      }}
    >
      <SectionHeader
        title="Transaction Count"
        right={
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: '#fff',
              background: RED,
              borderRadius: 5,
              padding: '3px 9px',
              letterSpacing: '.03em',
            }}
          >
            {limit} LIMIT
          </span>
        }
      />
      <div style={{ padding: '0 14px 14px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 2,
          }}
        >
          <span style={{ fontSize: 26, fontWeight: 800, color: '#0F172A' }}>
            {count.toLocaleString()}
          </span>
          <span style={{ fontSize: 12, color: '#94A3B8' }}>
            / {limit.toLocaleString()} threshold
          </span>
        </div>
        <div style={{ textAlign: 'right', marginBottom: 2 }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: pct >= 100 ? RED : accentColor }}>
            {pct.toFixed(1)}%
          </span>
        </div>
        <ProgressBar pct={pct} color={pct >= 100 ? RED : accentColor} />
      </div>
    </div>
  );
}

function RegisteredBanner({ filingStatus }: { filingStatus: string }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: '#F0FDF4',
        border: '1px solid #BBF7D0',
        borderRadius: 8,
        padding: '10px 14px',
        marginBottom: 12,
      }}
    >
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="7" stroke="#16A34A" strokeWidth="1.3" />
        <path
          d="M5 8l2 2 4-4"
          stroke="#16A34A"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#15803D' }}>Registered</span>
      {filingStatus && (
        <span style={{ fontSize: 12, color: '#15803D' }}>· Filing: {filingStatus}</span>
      )}
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: 260,
        gap: 10,
        padding: 24,
      }}
    >
      <svg aria-hidden="true" width="44" height="44" viewBox="0 0 44 44" fill="none">
        <circle cx="22" cy="22" r="20" stroke="#CBD5E1" strokeWidth="1.5" strokeDasharray="4 3" />
        <path
          d="M14 22h16M22 14l8 8-8 8"
          stroke="#CBD5E1"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span style={{ fontSize: 13, color: '#94A3B8', textAlign: 'center' }}>
        Select a state on the map to see details
      </span>
    </div>
  );
}

export default function StateDetailPanel({
  stateData,
}: { stateData: StateNexusData | null }): JSX.Element {
  if (!stateData) return <EmptyState />;

  const pct = stateData.revenueThresholdPct ?? 0;
  const isTriggered = stateData.nexusTriggered;
  const isRegistered = stateData.registrationStatus === 'registered';
  const accentColor = isTriggered ? RED : pct >= 80 ? AMBER : GREEN;

  const pillLabel = isTriggered
    ? 'Nexus Triggered'
    : pct >= 80
      ? `Watch List — ${pct.toFixed(0)}%`
      : 'Safe';
  const pillColor = isTriggered ? RED : pct >= 80 ? AMBER : GREEN;
  const pillBg = isTriggered ? '#FFF1F2' : pct >= 80 ? '#FFFBEB' : '#F0FDF4';
  const pillBorder = isTriggered ? '#FECDD3' : pct >= 80 ? '#FDE68A' : '#BBF7D0';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 16px',
          borderBottom: '1px solid #F1F5F9',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 7,
              background: accentColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 800, color: '#fff' }}>
              {stateData.stateCode}
            </span>
          </div>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#0F172A' }}>
            {stateData.stateName}
          </div>
        </div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            background: pillBg,
            border: `1px solid ${pillBorder}`,
            borderRadius: 20,
            padding: '4px 10px',
            fontSize: 11,
            fontWeight: 700,
            color: pillColor,
          }}
        >
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: pillColor }} />
          {pillLabel}
        </span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
        {isTriggered && !isRegistered && <RegistrationBanner stateCode={stateData.stateCode} />}
        {isTriggered && isRegistered && <RegisteredBanner filingStatus={stateData.filingStatus} />}

        <RevenueSection data={stateData} pct={pct} accentColor={accentColor} />
        {stateData.transactionThreshold != null && (
          <TransactionSection data={stateData} accentColor={accentColor} />
        )}

        {!isTriggered && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 12,
              color: '#64748B',
              padding: '8px 0',
            }}
          >
            <span>Registration status</span>
            <span style={{ fontWeight: 600, color: '#0F172A', textTransform: 'capitalize' }}>
              {stateData.registrationStatus?.replace('_', ' ') ?? '—'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
